import React from 'react';
import { Monitor, Wrench, ArrowLeftRight } from 'lucide-react';

// =====================================================================
// Chamados — A FAIXA DE FILA (Julia, 2026-09-17)
//
// A barra logo abaixo do cabeçalho que responde, sem a pessoa procurar: **em
// qual fila eu estou?** E, para quem alcança as duas, é também o único lugar de
// trocar.
//
// ---------------------------------------------------------------------
// ⚠️⚠️ O MÓDULO TROCA DE MATIZ NA FILA DE MANUTENÇÃO (17/09/2026)
// ---------------------------------------------------------------------
// Roxo no T.I, **índigo na Manutenção** — a cor do time dono da fila. A decisão
// é da Julia, e ela REVERTEU o desenho que durou algumas horas neste mesmo dia,
// em que só esta faixa levava o índigo e o módulo seguia roxo.
//
// ⚠️ O argumento que eu havia usado contra — "índigo é a cor do módulo
//    Manutenção/Certificação, a tela vai apontar para o lugar errado" — perdeu
//    força quando o título passou a dizer **"Chamados · Manutenção"** e esta
//    faixa passou a dizer **FILA: MANUTENÇÃO**: com os dois rótulos nomeando a
//    fila, a cor reforça o TIME, não o destino.
//
// ⚠️⚠️ A TROCA MORA EM VARIÁVEL CSS, não em prop: a cor estava fixa em 34
//    constantes de módulo, em 16 arquivos (`const COR = '#6b21a8'` no topo de
//    cada tela), e constante de módulo não lê prop nem contexto. O hub põe a
//    classe `modulo-chamados--manutencao` e TODAS as telas de dentro mudam
//    juntas. Ver o bloco "A COR DA FILA" no `index.css`.
//
// ⚠️ Esta faixa NÃO tem cor própria por isso: ela usa a cor do módulo como todo
//    o resto. Um índigo fixo aqui viraria a única cor que não acompanha a fila.
// =====================================================================

// A cor do módulo — que MUDA com a fila (roxo no T.I, índigo na Manutenção).
const COR = 'var(--ch-cor)';
const COR_CLARA = 'var(--ch-cor-clara)';

// O vocabulário de cada fila na tela. ⚠️ MAPA, jamais ternário: fila nova entra
// aqui e aparece em todo lugar: com `fila === 'Manutencao' ? … : …`, a terceira
// herdaria o rótulo do TI sem erro nenhum — a mesma armadilha que já mordeu o
// `rotuloDoEvento` deste módulo.
export const FILAS_TELA = {
    TI: {
        rotulo: 'T.I',
        icone: Monitor,
        cor: COR,
        fundo: COR_CLARA,
        // ⚠️ O que a pessoa vê depois de ler o nome da fila: o escopo dela, em
        //    linguagem de loja. É o que evita o chamado de fritadeira no TI.
        escopo: 'POS, KVS, impressora, rede, pinpad',
    },
    Manutencao: {
        rotulo: 'Manutenção',
        icone: Wrench,
        // ⚠️ A COR SAIU DAQUI em 17/09/2026: por algumas horas esta faixa foi o
        //    ÚNICO lugar com o índigo, emprestado como "sinal" enquanto o módulo
        //    seguia roxo. A Julia decidiu que o módulo INTEIRO troca de matiz na
        //    fila de Manutenção, então a faixa passou a usar a cor do módulo como
        //    todo o resto — e um índigo fixo aqui viraria a única cor que NÃO
        //    acompanha a fila.
        cor: COR,
        fundo: COR_CLARA,
        escopo: 'fritadeira, chapa, ar-condicionado, prédio',
    },
};

/** O rótulo de tela de uma fila, com o valor cru como último recurso. */
export function rotuloDaFila(fila) {
    return FILAS_TELA[fila]?.rotulo || fila || '—';
}

/**
 * A faixa de fila.
 *
 * @param {string} fila       a fila atual ('TI' | 'Manutencao')
 * @param {string[]} filas    as filas que esta pessoa alcança
 * @param {Function} onTrocar recebe a fila escolhida
 * @param {boolean} isMobile
 * @param {boolean} compacto  tablet ou menor: esconde o escopo, mantendo a linha
 *                            em UMA fileira (ícone + fila + botão)
 *
 * ⚠️⚠️ O BOTÃO DE TROCAR SÓ EXISTE COM MAIS DE UMA FILA, e isso não é economia
 *    de pixel: enquanto a fila de Manutenção está em implantação, quase todo
 *    mundo tem uma fila só — e um botão "trocar" que leva a um 403 é pior que
 *    botão nenhum. Quem decide a lista é o hub (`filasVisiveis`).
 *
 * ⚠️ A faixa aparece MESMO com uma fila só. Parece redundante ("é sempre T.I"),
 *    e é de propósito: ela é o lugar fixo onde a resposta mora. Se ela só
 *    surgisse na Manutenção, a ausência dela passaria a significar TI — e
 *    ausência não se lê, principalmente por quem nunca viu a outra.
 */
const ChamadosFaixaFila = ({ fila, filas = ['TI'], onTrocar, isMobile, compacto }) => {
    const atual = FILAS_TELA[fila] || FILAS_TELA.TI;
    const Icone = atual.icone;
    // A outra fila, quando há uma — é para ela que o botão leva.
    const outra = filas.find((f) => f !== fila);

    // ⚠️⚠️ NO CELULAR O BOTÃO VAI PARA UMA LINHA PRÓPRIA, INTEIRA. Com ele na
    //    mesma linha, o `marginLeft: auto` o empurrava para a direita — e quando
    //    não coubesse (aparelho de 360px, nome de fila longo) ele quebrava
    //    sozinho para a segunda linha **ainda colado à direita**, parecendo um
    //    botão solto no meio do nada.
    // ⚠️ E ganha altura de ALVO DE TOQUE (44px): quem troca de fila no aparelho é
    //    o técnico de pé na loja, muitas vezes de luva. O módulo já usa 40px de
    //    mínimo nas abas do celular; aqui vale o mesmo princípio.

    return (
        <div
            style={{
                display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12,
                flexWrap: 'wrap',
                // A faixa inteira ganha o tom da fila: é o sinal de longe.
                backgroundColor: atual.fundo,
                border: `1px solid ${atual.cor}22`,
                // ⚠️ A barra lateral grossa é o que faz a troca de fila ser
                //    percebida sem leitura — o olho pega a cor antes do texto.
                borderLeft: `5px solid ${atual.cor}`,
                borderRadius: 10,
                padding: isMobile ? '10px 12px' : '11px 16px',
                marginBottom: isMobile ? 12 : 16,
            }}
        >
            <Icone size={isMobile ? 18 : 19} color={atual.cor} style={{ flexShrink: 0 }} />

            {/* ⚠️ `minWidth: 0` + `overflow` no nome da fila: sem isso, um rótulo
                comprido empurraria o botão para fora da faixa no aparelho, em vez
                de encolher. */}
            <span style={{
                fontSize: 13, fontWeight: 800, color: atual.cor,
                textTransform: 'uppercase', letterSpacing: '0.04em',
                minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
                Fila: {atual.rotulo}
            </span>

            {/* ⚠⚠ O ESCOPO SÓ NO COMPUTADOR LARGO. No celular ele empurraria o
                botão para uma terceira linha; no TABLET (769–1024px, que usa o
                layout de computador) ele faz a linha estourar e o botão quebrar
                sozinho para baixo, colado à direita — o mesmo desenho torto que o
                celular ganhou conserto acima. O nome da fila e o ícone, que são o
                recado, ficam nos três tamanhos. */}
            {!compacto && (
                <span style={{ fontSize: 12.5, color: '#64748b' }}>
                    {atual.escopo}
                </span>
            )}

            {outra && (
                <button
                    type="button"
                    onClick={() => onTrocar(outra)}
                    style={{
                        // Computador: à direita, na mesma linha. Celular: linha
                        // própria, largura inteira e alvo de toque de verdade.
                        ...(isMobile
                            ? { width: '100%', minHeight: 44, justifyContent: 'center', marginTop: 4 }
                            : { marginLeft: 'auto' }),
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: isMobile ? '8px 12px' : '7px 13px',
                        borderRadius: 8, cursor: 'pointer',
                        border: `1px solid ${atual.cor}33`,
                        backgroundColor: '#fff',
                        color: atual.cor,
                        fontSize: 12.5, fontWeight: 700,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <ArrowLeftRight size={14} />
                    {/* ⚠️ O rótulo DIZ O DESTINO ("Ir para Manutenção"), não o ato
                        ("Trocar"): num botão de duas filas, "trocar" obriga a
                        pessoa a deduzir para onde vai.
                        ⚠️ E ele diz isso TAMBÉM no celular: a versão curta ("só o
                        nome da fila") economizava dois centímetros e devolvia um
                        botão ambíguo — "Manutenção" sozinho tanto pode levar
                        quanto indicar onde se está. */}
                    Ir para {rotuloDaFila(outra)}
                </button>
            )}
        </div>
    );
};

export default ChamadosFaixaFila;
